import { useCallback, useEffect, useRef, useState } from "react";
import { avisarError } from "../../shared/errores";
import { MAX_DOCUMENTO_MB, eliminarDocumento, enlaceDescarga, listarDocumentos, subirDocumento } from "./proyectosService";
import { Etiqueta } from "./componentes";
import { botonFantasma, botonPeligro, filaResaltable, icono, panel, pequeno, tituloPanel } from "./estilos";
import { fechaHora, tamanoArchivo, tipoArchivo } from "./reglas";
import { confirmar } from "../../shared/avisos";
import Esqueleto from "../../shared/components/Esqueleto";

const MAX_MB = MAX_DOCUMENTO_MB;

function Miniatura({ adjunto, tamano }) {
  if (adjunto.miniatura) {
    return <img src={adjunto.miniatura} alt="" loading="lazy" className={`${tamano} shrink-0 rounded-lg border border-white/10 object-cover`} />;
  }
  const tipo = tipoArchivo(adjunto.nombre);
  return <span className={`${tamano} flex shrink-0 items-center justify-center rounded-lg text-[10px] font-black ${tipo.clase}`}>{tipo.texto}</span>;
}

// Documentos de un proyecto (incluye los de sus tareas) o de una tarea.
// "destino" es { proyecto_id } o { tarea_id }: de dónde se listan y adónde se suben.
// "compacto" es la versión dentro de la ventana de una tarea.
function Documentos({ destino, puedeSubir, puedeBorrar, tituloTarea, onAbrirTarea, compacto = false, onCambio }) {
  const [adjuntos, setAdjuntos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [subiendo, setSubiendo] = useState(0);
  const [encima, setEncima] = useState(false);
  const selector = useRef(null);
  const consulta = new URLSearchParams(destino).toString();

  const cargar = useCallback(() => {
    listarDocumentos(Object.fromEntries(new URLSearchParams(consulta)))
      .then(setAdjuntos)
      .catch(avisarError)
      .finally(() => setCargando(false));
  }, [consulta]);

  useEffect(() => { cargar(); }, [cargar]);

  const subir = async (archivos) => {
    const lista = [...archivos];
    const grandes = lista.filter((a) => a.size > MAX_MB * 1024 * 1024);
    if (grandes.length) avisarError(new Error(`${grandes.map((a) => a.name).join(", ")} supera los ${MAX_MB} MB.`));
    const validos = lista.filter((a) => a.size <= MAX_MB * 1024 * 1024);
    setSubiendo((n) => n + validos.length);
    for (const archivo of validos) {
      try {
        await subirDocumento(destino, archivo);
      } catch (error) {
        avisarError(error);
      } finally {
        setSubiendo((n) => n - 1);
      }
    }
    cargar();
    onCambio?.();
  };

  const soltar = (e) => {
    e.preventDefault();
    setEncima(false);
    if (puedeSubir && e.dataTransfer.files.length) subir(e.dataTransfer.files);
  };

  const descargar = async (adjunto) => {
    try {
      const url = await enlaceDescarga(adjunto);
      window.open(url, "_blank", "noopener");
    } catch (error) {
      avisarError(error);
    }
  };

  const eliminar = async (adjunto) => {
    if (!(await confirmar(`¿Eliminar el documento "${adjunto.nombre}"?`, { aceptar: "Eliminar" }))) return;
    try {
      await eliminarDocumento(adjunto);
      cargar();
      onCambio?.();
    } catch (error) {
      avisarError(error);
    }
  };

  const elegir = (
    <input ref={selector} type="file" multiple className="hidden" onChange={(e) => { subir(e.target.files); e.target.value = ""; }} />
  );
  const arrastre = {
    onDragOver: (e) => { if (puedeSubir) { e.preventDefault(); setEncima(true); } },
    onDragLeave: (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setEncima(false); },
    onDrop: soltar,
  };

  const fila = (a) => (
    <div key={a.id} className={`flex items-center gap-4 px-5 py-3 ${filaResaltable} ${compacto ? "!gap-3 rounded-xl border border-white/10 bg-zinc-900 !px-3 !py-2" : ""}`}>
      <Miniatura adjunto={a} tamano={compacto ? "h-9 w-9" : "h-11 w-11"} />
      <div className="min-w-0 flex-1">
        <button type="button" onClick={() => descargar(a)} className="block max-w-full truncate text-left text-sm font-semibold hover:underline">{a.nombre}</button>
        <p className="truncate text-xs text-gray-500">{[tamanoArchivo(a.tamano), a.subido_por, fechaHora(a.creado_en)].filter(Boolean).join(" · ")}</p>
      </div>
      {!compacto && a.tarea_id && (
        <button type="button" onClick={() => onAbrirTarea?.(a.tarea_id)} className="hidden max-w-[14rem] truncate sm:inline-flex">
          <Etiqueta clase="border-white/15 text-gray-300 hover:bg-white hover:text-black">Tarea: {tituloTarea?.(a.tarea_id)}</Etiqueta>
        </button>
      )}
      {!compacto && !a.tarea_id && <span className="hidden sm:inline-flex"><Etiqueta clase="border-white/15 text-gray-500">Proyecto</Etiqueta></span>}
      <button type="button" onClick={() => descargar(a)} title="Descargar" aria-label={`Descargar ${a.nombre}`} className={`${botonFantasma} ${pequeno} ${icono}`}>↓</button>
      {puedeBorrar(a) && (
        <button type="button" onClick={() => eliminar(a)} title="Eliminar" aria-label={`Eliminar ${a.nombre}`} className={`${botonPeligro} ${pequeno} ${icono} !text-base`}>×</button>
      )}
    </div>
  );

  if (compacto) {
    return (
      <div>
        <div className="mb-4 flex items-center justify-between">
          <h4 className={tituloPanel}>Documentos</h4>
          {puedeSubir && (
            <button type="button" onClick={() => selector.current?.click()} disabled={subiendo > 0} className={`${botonFantasma} ${pequeno}`}>
              {subiendo ? `Subiendo ${subiendo}…` : "+ Adjuntar"}
            </button>
          )}
          {elegir}
        </div>
        <div {...arrastre} className={`rounded-xl border-2 border-dashed transition-colors ${encima ? "border-white/60 bg-white/5" : "border-transparent"}`}>
          {!cargando && !adjuntos.length && (
            <p className="px-1 py-3 text-sm text-gray-500">
              {puedeSubir ? "Sin documentos. Arrastra archivos aquí o usa «Adjuntar»." : "Sin documentos."}
            </p>
          )}
          <div className="space-y-2">{adjuntos.map(fila)}</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {puedeSubir && (
        <button
          type="button"
          {...arrastre}
          onClick={() => selector.current?.click()}
          className={`mb-6 block w-full cursor-pointer rounded-2xl border-2 border-dashed p-8 text-center transition-colors ${encima ? "border-white/60 bg-white/5" : "border-white/15 hover:border-white/40"}`}
        >
          <p className="text-sm font-bold uppercase tracking-widest">{subiendo ? `Subiendo ${subiendo}…` : "Arrastra documentos aquí o haz clic para elegir"}</p>
          <p className="mt-1 text-xs text-gray-500">Cualquier tipo de archivo, hasta {MAX_MB} MB cada uno</p>
        </button>
      )}
      {elegir}
      {cargando ? (
        <Esqueleto cantidad={2} texto="Cargando documentos…" />
      ) : !adjuntos.length ? (
        <p className="py-6 text-center text-sm text-gray-500">Este proyecto aún no tiene documentos.</p>
      ) : (
        <div className={`${panel} divide-y divide-white/5`}>{adjuntos.map(fila)}</div>
      )}
    </div>
  );
}

export default Documentos;
